import React, { useState, useEffect } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from "firebase/firestore";

import { db } from "../Context/firebaseConfig";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";

import {
  MessageCircle,
  Play,
  Eye,
  Clock,
  Instagram,
  Youtube,
  Twitch,
  Twitter,
  Sparkles,
  Workflow,
  HeartHandshake,
  AlertTriangle,
  Trophy,
  ArrowRight,
  User as UserIcon,
} from "lucide-react";

import { FaTiktok } from "react-icons/fa";
import SEO, { breadcrumbSchema, profilePageSchema } from "./SEO";
import { getUserEntitlements } from "../config/plans";
import {
  getActiveEditorProfileBannerUrl,
  getActiveEditorProfileTheme,
} from "../utils/editorProfileAppearance";
import { normalizeSocialUrl } from "../utils/socialLinks";
import {
  listPublicProfiles,
  recordPublicProfileView,
} from "../services/publicProfiles";

const PURPLE = "#9146FF";
const GREEN = "#84ff00";
const BG = "#07060d";

function GlowBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      <div
        className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full blur-3xl opacity-40"
        style={{
          background: `radial-gradient(circle, ${PURPLE} 0%, transparent 60%)`,
        }}
      />

      <div
        className="absolute top-20 -right-32 h-[480px] w-[480px] rounded-full blur-3xl opacity-30"
        style={{
          background: `radial-gradient(circle, ${GREEN} 0%, transparent 60%)`,
        }}
      />

      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
    </div>
  );
}

function NicheBadge({ label, color, background, border}) {
  if (!label) return null;

  return (
    <span
      className="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold backdrop-blur"
      style={{
        color: color,
        background: background,
        border: border,
      }}
    >
      {label}
    </span>
  );
}

function SocialBtn({ icon: Icon, href, platform }) {
  const socialUrl = normalizeSocialUrl(platform, href);
  if (!socialUrl) return null;

  return (
    <a
      href={socialUrl}
      target="_blank"
      rel="noreferrer"
      className="grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-zinc-300 backdrop-blur transition hover:scale-110 hover:border-white/30 hover:text-white"
      style={{
        boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
      }}
    >
      <Icon className="h-4 w-4" />
    </a>
  );
}

function VideoCard({ v, large = false, convertDuration }) {
  return (
    <a
      href={`https://www.youtube.com/watch?v=${v.id}`}
      target="_blank"
      rel="noopener noreferrer"
      className={`group relative block overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition hover:-translate-y-1 hover:border-white/20 ${
        large
          ? "aspect-video w-full"
          : "aspect-video w-[78vw] sm:w-[340px] flex-shrink-0"
      }`}
      style={{
        boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
      }}
    >
      <img
        src={v.snippet.thumbnails.high.url}
        alt={v.snippet.title}
        className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />

      <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-md bg-black/70 px-2 py-1 text-[11px] font-medium text-white backdrop-blur">
        <Clock className="h-3 w-3" />
        {convertDuration(v.contentDetails.duration)}
      </span>

      <div className="absolute inset-0 grid place-items-center">
        <div
          className="grid h-14 w-14 place-items-center rounded-full backdrop-blur-md transition group-hover:scale-110"
          style={{
            background: `${GREEN}E0`,
            boxShadow: `0 0 30px ${GREEN}80`,
          }}
        >
          <Play className="h-6 w-6 fill-black text-black" />
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-4">
        <h4
          className={`line-clamp-2 font-semibold text-white ${
            large ? "text-lg sm:text-xl" : "text-sm"
          }`}
        >
          {v.snippet.title}
        </h4>

        <div className="mt-1.5 flex items-center gap-3 text-[11px] text-zinc-300">
          <span className="inline-flex items-center gap-1">
            <Eye className="h-3 w-3" />
            {Number(v.statistics.viewCount).toLocaleString()}
          </span>

          <span>•</span>

          <span>
            {new Date(v.snippet.publishedAt).toLocaleDateString()}
          </span>
        </div>
      </div>
    </a>
  );
}

function QuestionCard({ icon: Icon, label, title, body, accent }) {
  return (
    <div
      className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl transition hover:-translate-y-0.5 hover:border-white/20 sm:p-6"
      style={{
        boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
      }}
    >
      <div
        className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full blur-3xl opacity-30 transition group-hover:opacity-50"
        style={{
          background: accent,
        }}
      />

      <div className="flex items-start gap-4">
        <div
          className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-2xl"
          style={{
            background: `${accent}18`,
            border: `1px solid ${accent}55`,
            color: accent,
          }}
        >
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0">
          <p
            className="text-[11px] font-semibold uppercase tracking-wider"
            style={{ color: accent }}
          >
            {label}
          </p>

          <h4 className="mt-1 text-base font-bold text-white sm:text-lg">
            {title}
          </h4>

          <p className="mt-2 text-sm leading-relaxed text-zinc-400">
            {body}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function EditorProfile({ editorName }) {
  const [editor, setEditor] = useState(null);
  const [videos, setVideos] = useState([]);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [readMore, setReadMore] = useState(false);

  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchEditor = async () => {
      try {
        const profiles = await listPublicProfiles({ username: editorName });
        const userData = profiles[0];

        if (userData) {
          setUserId(userData.id);
          setEditor(userData);

          const videoLinks = userData.videoLinks;

          if (videoLinks) {
            fetchVideoData(Object.values(videoLinks));
          }
        } else {
          setError("No editor found");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load editor");
      }
    };

    const fetchVideoData = async (urls) => {
      try {
        const videoData = await Promise.all(
          urls.map(async (url) => {
            const videoId = extractYouTubeId(url);

            if (!videoId) return null;

            const response = await fetch(
              `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&key=${process.env.REACT_APP_YOUTUBE_API_KEY}&part=snippet,contentDetails,statistics`
            );

            const data = await response.json();

            if (data.items && data.items.length > 0) {
              return data.items[0];
            }

            return null;
          })
        );

        const filtered = videoData
          .filter((v) => v !== null)
          .sort(
            (a, b) =>
              Number(b.statistics.viewCount) -
              Number(a.statistics.viewCount)
          );

        setVideos(filtered);
      } catch (error) {
        console.error(error);
      }
    };

    const extractYouTubeId = (url) => {
      const regExp =
        /^https:\/\/www\.youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})(?=$|[&])/;

      const match = url.match(regExp);

      return match ? match[1] : null;
    };

    fetchEditor();
  }, [editorName]);

  useEffect(() => {
    const trackProfileView = async () => {
      if (!userId) return;
      if (currentUser?.uid && currentUser.uid === userId) return;

      const dayKey = new Date().toISOString().slice(0, 10);
      const storageKey = `kre8_editor_profile_view_${userId}_${dayKey}`;

      if (window.localStorage.getItem(storageKey)) {
        return;
      }

      try {
        await recordPublicProfileView(userId);
        window.localStorage.setItem(storageKey, "1");
      } catch (viewError) {
        console.error("Failed to track editor profile view:", viewError);
      }
    };

    trackProfileView();
  }, [currentUser, userId]);

  const createChat = async () => {
    if (!currentUser || !userId) {
      navigate("/login");
      return;
    }

    try {
      const chatsRef = collection(db, "chats");

      const q = query(
        chatsRef,
        where("members", "array-contains", currentUser.uid)
      );

      const querySnapshot = await getDocs(q);

      let existingChatId = null;

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        if (data.members.includes(userId)) {
          existingChatId = doc.id;
        }
      });

      if (existingChatId) {
        navigate({
          pathname: "/chat",
          search: `?conversationId=${existingChatId}`,
        });

        return;
      }

      const currentUserDoc = await getDoc(
        doc(db, "Users", currentUser.uid)
      );

      const currentUserInfo = currentUserDoc.data();

      const friendUserInfo = editor;

      const chatKey = `${currentUser.uid}_${userId}`;

      await setDoc(doc(db, "chats", chatKey), {
        members: [currentUser.uid, userId],
        lastMessage: "",
        timestamp: new Date(),

        Users: [
          {
            uid: currentUser.uid,
            username:
              currentUserInfo?.profileInfo?.username || "",
            profilePicUrl:
              currentUserInfo?.profileInfo?.profilePicUrl || "",
          },

          {
            uid: userId,
            username:
              friendUserInfo?.profileInfo?.username || "",
            profilePicUrl:
              friendUserInfo?.profileInfo?.profilePicUrl || "",
          },
        ],
      });

      await setDoc(doc(db, `Users/${currentUser.uid}/chats`, chatKey), {
        chatId: chatKey,
        timestamp: new Date(),
      });

      await setDoc(doc(db, `Users/${userId}/chats`, chatKey), {
        chatId: chatKey,
        timestamp: new Date(),
      });

      navigate({
        pathname: "/chat",
        search: `?conversationId=${chatKey}`,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const convertDuration = (duration) => {
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);

    const hours = parseInt(match[1]) || 0;
    const minutes = parseInt(match[2]) || 0;
    const seconds = parseInt(match[3]) || 0;

    return `${hours > 0 ? `${hours}:` : ""}${minutes}:${
      seconds < 10 ? "0" : ""
    }${seconds}`;
  };

  if (error) {
    return (
      <>
        <SEO
          title="Editor Profile Not Found | Kre8"
          description="This Kre8 editor profile could not be found."
          canonicalPath={`/editor/${editorName}`}
          noindex
        />
        <main
          className="min-h-screen flex items-center justify-center px-6"
          style={{ backgroundColor: BG }}
        >
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white">
              Something went wrong
            </h1>

            <p className="mt-2 text-zinc-400">{error}</p>
          </div>
        </main>
      </>
    );
  }

  if (!editor) {
    return (
      <main
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: BG }}
      >
        <div className="flex flex-col items-center">
          <div
            className="h-16 w-16 rounded-full border-4 border-t-transparent animate-spin"
            style={{ borderColor: PURPLE }}
          />

          <p className="mt-5 text-zinc-400">
            Loading profile...
          </p>
        </div>
      </main>
    );
  }

  const questions = [
    {
      icon: Workflow,
      label: "Best workflow",
      title: "How I run our collaboration",
      body: editor.profileInfo.questions?.questionOne,
      accent: GREEN,
    },

    {
      icon: HeartHandshake,
      label: "Easy to work with",
      title: "How I make collab effortless",
      body: editor.profileInfo.questions?.questionTwo,
      accent: PURPLE,
    },

    {
      icon: AlertTriangle,
      label: "Watch-outs",
      title: "Potential workflow pain points",
      body: editor.profileInfo.questions?.questionThree,
      accent: "#fbbf24",
    },

    {
      icon: Trophy,
      label: "What I bring",
      title: "What I bring to your content",
      body: editor.profileInfo.questions?.questionFour,
      accent: GREEN,
    },
  ];
  const profile = editor.profileInfo || {};
  const username = profile.username || editorName || "editor";
  const mainGame =
    typeof profile.mainGame === "object"
      ? profile.mainGame?.categoryName || profile.mainGame?.name
      : profile.mainGame;
  const profileSummary = (profile.tagline || profile.about || "")
    .replace(/\s+/g, " ")
    .trim();
  const conciseSummary =
    profileSummary.length > 80
      ? `${profileSummary.slice(0, 77)}...`
      : profileSummary;
  const title = `@${username} | ${
    mainGame ? `${mainGame} Twitch & Gaming Editor` : "Twitch & Gaming Video Editor"
  } | Kre8`;
  const description = conciseSummary
    ? `${conciseSummary} Twitch highlight, stream clip, and gaming video editor on Kre8.`
    : `Hire @${username} for ${
        mainGame ? `${mainGame} gaming edits, ` : ""
      }Twitch highlights, stream clips, YouTube gaming videos, and creator content on Kre8.`;
  const sameAs = [
    editor.socials?.youtube,
    editor.socials?.twitch,
    editor.socials?.instagram,
    editor.socials?.twitter,
    editor.socials?.tiktok,
  ];
  const entitlements = getUserEntitlements(editor);
  const activeTheme = getActiveEditorProfileTheme(editor);
  const activeBannerUrl = getActiveEditorProfileBannerUrl(editor);

  return (
    <>
      <SEO
        title={title}
        description={description}
        canonicalPath={`/editor/${username}`}
        image={profile.profilePicUrl || undefined}
        type="profile"
        structuredData={[
          profilePageSchema({
            name: `@${username}`,
            description,
            path: `/editor/${username}`,
            image: profile.profilePicUrl,
            sameAs,
            role: "Video Editor",
            knowsAbout: [
              mainGame,
              "Twitch highlights",
              "Gaming video editing",
              "Stream clips",
              "YouTube gaming videos",
            ].filter(Boolean),
          }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Twitch Editors", path: "/gaming-video-editors" },
            { name: `@${username}`, path: `/editor/${username}` },
          ]),
        ]}
      />
      <main
        className="relative min-h-screen overflow-x-hidden pb-24"
        style={{
          backgroundColor: BG,
          color: "#e5e7eb",
        }}
      >
      <GlowBackdrop />

      {/* HERO */}
      <section className="relative">
        <div className="relative mx-auto max-w-6xl px-4 pt-10 pb-12 sm:px-6 sm:pt-16 sm:pb-20">
          <div
            className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-2xl sm:p-10"
            style={{
              boxShadow: "0 20px 80px rgba(0,0,0,0.5)",
              background: activeTheme.surface,
            }}
          >
            {activeBannerUrl && (
              <>
                <img
                  src={activeBannerUrl}
                  alt="editor profile banner"
                  className="absolute inset-0 h-full w-full object-cover opacity-35"
                />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(7,6,13,0.2) 0%, rgba(7,6,13,0.84) 70%, rgba(7,6,13,0.96) 100%)",
                  }}
                />
              </>
            )}
            <div className="flex flex-col items-center gap-6 text-center lg:flex-row lg:items-start lg:gap-10 lg:text-left">
              <div className="relative flex-shrink-0">
                <div
                  className="absolute inset-0 rounded-full blur-2xl opacity-70"
                  style={{
                    background: activeTheme.ring,
                  }}
                />

                <div
                  className="relative rounded-full p-1"
                  style={{
                    background: activeTheme.ring,
                  }}
                >
                  {editor.profileInfo?.profilePicUrl ? (
                    <img
                      src={editor.profileInfo.profilePicUrl}
                      alt="profile"
                      className="h-28 w-28 rounded-full object-cover sm:h-36 sm:w-36"
                    />
                  ) : (
                    <div className="grid h-28 w-28 place-items-center rounded-full bg-[#0d0a18] sm:h-36 sm:w-36">
                      <UserIcon className="h-12 w-12 text-zinc-500 sm:h-16 sm:w-16" />
                    </div>
                  )}
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <h1 className="text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
                  @{editor.profileInfo?.username}
                </h1>

                <p className="mt-2 text-base text-zinc-400 sm:text-lg">
                  {editor.profileInfo?.tagline}
                </p>

                <div className="mt-5 flex flex-wrap justify-center gap-2 lg:justify-start">
                  <NicheBadge
                    label={editor.profileInfo?.role}
                    color={PURPLE}
                    background={`${PURPLE}15`}
                    border={`1px solid ${PURPLE}55`}
                  />
                  <NicheBadge
                    label={mainGame}
                    color={activeTheme.secondary}
                    background={`${activeTheme.secondary}15`}
                    border={`1px solid ${activeTheme.secondary}55`}
                  />
                  {entitlements.featuredPlacement && (
                    <NicheBadge
                      label="Featured Editor"
                      color={activeTheme.secondary}
                      background={`${activeTheme.secondary}20`}
                      border={`1px solid ${activeTheme.secondary}80`}
                    />
                  )}
                </div>

                <div className="mt-5 flex flex-wrap justify-center gap-2 lg:justify-start">
                  <SocialBtn
                    icon={Youtube}
                    href={editor.socials?.youtube}
                    platform="youtube"
                  />

                  <SocialBtn
                    icon={Twitch}
                    href={editor.socials?.twitch}
                    platform="twitch"
                  />

                  <SocialBtn
                    icon={Instagram}
                    href={editor.socials?.instagram}
                    platform="instagram"
                  />

                  <SocialBtn
                    icon={Twitter}
                    href={editor.socials?.twitter}
                    platform="twitter"
                  />

                  <SocialBtn
                    icon={FaTiktok}
                    href={editor.socials?.tiktok}
                    platform="tiktok"
                  />
                </div>

                <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center lg:justify-start">
                  <button
                    onClick={createChat}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold text-black transition hover:scale-105"
                    style={{
                      background: GREEN,
                      boxShadow: `0 0 30px ${GREEN}80`,
                    }}
                  >
                    <MessageCircle className="h-4 w-4" />
                    Message Editor
                  </button>
                </div>
              </div>
            </div>

            {/* ABOUT */}
            <div className="mt-8 rounded-2xl border border-white/10 bg-black/30 p-5 backdrop-blur sm:p-6">
              <div className="mb-3 flex items-center gap-2">
                <Sparkles
                  className="h-4 w-4"
                  style={{ color: activeTheme.secondary }}
                />

                <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-300">
                  About
                </h3>
              </div>

              <p
                className={`text-sm leading-relaxed text-zinc-300 sm:text-base ${
                  readMore ? "" : "line-clamp-3"
                }`}
              >
                {editor.profileInfo?.about}
              </p>

              <button
                onClick={() => setReadMore((s) => !s)}
                className="mt-2 text-xs font-semibold"
                style={{ color: activeTheme.secondary }}
              >
                {readMore ? "Show less" : "Read more"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* VIDEOS */}
      <section className="relative mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
            Top Performing Videos
          </h2>

          <p className="mt-1 text-sm text-zinc-400">
            Most viewed edits & creator collaborations
          </p>
        </div>

        {videos.length > 0 && (
          <>
            <VideoCard
              v={videos[0]}
              large
              convertDuration={convertDuration}
            />

            <div className="mt-6 -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
              <div className="flex gap-4 pb-2">
                {videos.slice(1).map((video) => (
                  <VideoCard
                    key={video.id}
                    v={video}
                    convertDuration={convertDuration}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </section>

      <section className="relative mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
            How we'd work together
          </h2>

          <p className="mt-1 text-sm text-zinc-400">
            Workflow, expectations, and collaboration style.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {questions.map((q) => (
            <QuestionCard
              key={q.title}
              icon={q.icon}
              label={q.label}
              title={q.title}
              body={q.body}
              accent={q.accent}
            />
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative mx-auto max-w-6xl px-4 pb-32 sm:px-6 sm:pb-24">
        <div
          className="relative overflow-hidden rounded-3xl border border-white/10 p-8 text-center sm:p-12"
          style={{
            background: `radial-gradient(circle at 30% 20%, ${PURPLE}30 0%, transparent 60%), radial-gradient(circle at 80% 80%, ${GREEN}25 0%, transparent 60%), #0d0a18`,
            boxShadow: "0 20px 80px rgba(0,0,0,0.5)",
          }}
        >
          <h3 className="text-2xl font-extrabold tracking-tight text-white sm:text-4xl">
            Ready to scale your content?
          </h3>

          <p className="mx-auto mt-3 max-w-md text-sm text-zinc-300 sm:text-base">
            Message{" "}
            <span className="font-semibold text-white">
              @{editor.profileInfo?.username}
            </span>{" "}
            and start collaborating today.
          </p>

          <button
            onClick={createChat}
            className="mt-6 inline-flex items-center gap-2 rounded-full px-7 py-4 text-base font-bold text-black transition hover:scale-105"
            style={{
              background: GREEN,
              boxShadow: `0 0 40px ${GREEN}90`,
            }}
          >
            <MessageCircle className="h-5 w-5" />
            Message @{editor.profileInfo?.username}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      {/* MOBILE CTA */}
      <div
        className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#07060d]/95 px-4 py-3 backdrop-blur-xl sm:hidden"
        style={{
          boxShadow: "0 -10px 30px rgba(0,0,0,0.6)",
        }}
      >
        <button
          onClick={createChat}
          className="flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold text-black"
          style={{
            background: GREEN,
            boxShadow: `0 0 30px ${GREEN}90`,
          }}
        >
          <MessageCircle className="h-4 w-4" />
          Message Editor
        </button>
      </div>
      </main>
    </>
  );
}
